import React, {
  useContext,
  useCallback,
  useState,
  useRef,
  useEffect,
  useMemo,
} from "react";
import { useDropzone } from "react-dropzone";
import Papa from "papaparse";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  Box,
  ModalHeader,
  Divider,
  ModalBody,
  ModalFooter,
  HStack,
  Button,
  Text,
  Badge,
  useToast,
  FormControl,
  Table,
  Thead,
  Tbody,
  Select as ChakraSelect,
  Tr,
  Th,
  Td,
  VStack,
  Icon,
  Heading,
  InputGroup,
  Input,
} from "@chakra-ui/react";
import { FaFileCsv } from "react-icons/fa";
import actions from "src/actions";
import ReactS3Uploader, { S3Response } from "react-s3-uploader";
import { useForm, useFormContext } from "react-hook-form";
import { createDialogContext, DialogProvider } from "src/Common/Dialog";

export const CreateAudienceFromCsvDialogContext = createDialogContext();

function getCsvModalDefaultValues() {
  return {
    name: "",
    description: "",
    mapping: [] as string[],
    file_key: "",
  };
}

// Audience schema fields for Meta/Facebook
const AUDIENCE_SCHEMA_FIELDS = [
  { label: "First Name (FN)", value: "FN" },
  { label: "Last Name (LN)", value: "LN" },
  { label: "Email (EMAIL)", value: "EMAIL" },
  { label: "Phone (PHONE)", value: "PHONE" },
  { label: "Country (COUNTRY)", value: "COUNTRY" },
];

interface CreateAudienceFromCsvDialogProps {
  title?: string;
  isOpen: boolean;
  close: () => void;
  onSubmit?: (audienceData: {
    id: string;
    name: string;
    description: string;
    file_key: string;
    mapping: string[];
  }) => void;
}

const CreateAudienceFromCsvDialog = ({
  title = "Upload customer data",
  isOpen,
  onSubmit = () => {},
  close,
}: CreateAudienceFromCsvDialogProps) => {
  const toast = useToast();
  const parentForm = useFormContext(); // Get the parent form context
  const { register, handleSubmit, reset, setValue, watch } = useForm({
    defaultValues: getCsvModalDefaultValues(),
  });

  const [file, setFile] = useState<File | null>(null);
  const [parsedHeaders, setParsedHeaders] = useState<string[]>([]);
  const [parsedRows, setParsedRows] = useState<string[][]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  /** Bumped on reset so in-flight Papa.parse callbacks cannot repopulate after close. */
  const parseSessionRef = useRef(0);

  const setMapping = useCallback(
    (mapping: string[]) => {
      setValue("mapping", mapping);
    },
    [setValue]
  );

  const resetCsvModal = useCallback(() => {
    parseSessionRef.current += 1;
    setFile(null);
    setParsedHeaders([]);
    setParsedRows([]);
    setIsUploading(false);
    if (inputRef.current) {
      inputRef.current.value = "";
    }
    reset(getCsvModalDefaultValues());
  }, [reset]);

  useEffect(() => {
    if (!isOpen) {
      resetCsvModal();
    }
  }, [isOpen, resetCsvModal]);

  const handleModalClose = useCallback(() => {
    resetCsvModal();
    close();
  }, [close, resetCsvModal]);

  const mapping = watch("mapping");
  const name = watch("name");
  const description = watch("description");

  const onDrop = useCallback(
    (accepted: File[]) => {
      if (!accepted.length) return;
      const f = accepted[0];
      const session = parseSessionRef.current;
      setFile(f);

      Papa.parse<string[]>(f, {
        preview: 11,
        skipEmptyLines: true,
        complete: (results) => {
          if (session !== parseSessionRef.current) return;
          const data = results.data;
          if (data.length < 2) {
            toast({ status: "error", title: "CSV must have at least 2 rows" });
            setFile(null);
            return;
          }
          setParsedHeaders(data[0]);
          setParsedRows(data.slice(1));
          setMapping(data[0].map(() => ""));
        },
        error: (err) => {
          if (session !== parseSessionRef.current) return;
          toast({
            status: "error",
            title: "Parse error",
            description: err.message,
          });
        },
      });
    },
    [toast, setMapping]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    accept: { "text/csv": [".csv"] },
  });

  const handleImportClick = () => {
    if (!file || !inputRef.current) return;
    setIsUploading(true);

    const dt = new DataTransfer();
    dt.items.add(file);
    inputRef.current.files = dt.files as any;

    inputRef.current.dispatchEvent(new Event("change", { bubbles: true }));
  };

  const isFormValid = useMemo(() => {
    return (
      file &&
      (mapping.includes("FN") || mapping.includes("LN")) &&
      mapping.includes("EMAIL") &&
      !isUploading &&
      name
    );
  }, [file, mapping, isUploading, name]);

  const _onSubmit = async (data: {
    name: string;
    description: string;
    mapping: string[];
    file_key: string;
  }) => {
    if (!data.file_key) {
      toast({
        status: "error",
        title: "Upload failed",
        description: "No fileKey returned.",
      });
      return;
    }

    try {
      // Create audience data object to be added to the form
      const audienceData = {
        id: `csv_audience_${Date.now()}`, // Generate a temporary ID
        name: data.name,
        description: data.description,
        file_key: data.file_key,
        mapping: data.mapping,
        schema_fields: data.mapping.filter((field) => field !== ""), // Only include mapped fields
        is_csv_audience: true, // Flag to identify this as a CSV audience
        created_at: new Date().toISOString(),
      };

      // Get current custom audience files from the parent form
      const currentCustomAudienceFiles =
        parentForm.getValues("custom_audience_files") || [];

      // Add the new CSV audience to the list
      const updatedCustomAudienceFiles = [
        ...currentCustomAudienceFiles,
        audienceData,
      ];

      // Update the parent form with the new audience data
      parentForm.setValue("custom_audience_files", updatedCustomAudienceFiles);

      toast({
        status: "success",
        title: "CSV Audience Added",
        description:
          "The CSV audience has been added to your campaign. It will be processed when you publish the campaign.",
      });

      resetCsvModal();
      close();
      onSubmit(audienceData);
    } catch (error) {
      console.error("Error adding CSV audience:", error);
      toast({
        status: "error",
        title: "Failed to add audience",
        description: "Please try again.",
      });
    }
  };

  const onUploadFinish = (res: S3Response) => {
    setIsUploading(false);

    if (res.fileKey) {
      console.log({
        res,
      });
      setValue("file_key", res.fileKey);
      handleSubmit(_onSubmit)();
    } else {
      toast({
        status: "error",
        title: "Upload failed",
        description: "No fileKey returned.",
      });
    }
  };

  const onUploadError = (err: Error) => {
    setIsUploading(false);
    toast({
      status: "error",
      title: "Upload to S3 failed",
      description: err.message,
    });
  };

  const handleCancel = () => {
    handleModalClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleModalClose} size="3xl" isCentered>
      <ModalOverlay />
      <ModalContent bg="white" borderRadius="lg" p={0}>
        <Box p={6}>
          <ModalHeader p={0} mb={4} fontSize="2xl">
            <HStack justify="space-between">
              <Text fontWeight="400" fontSize="24px">
                {title}
              </Text>
              <HStack spacing={3}>
                <Button
                  variant="outline"
                  rounded="full"
                  fontSize="13px"
                  onClick={handleCancel}
                  isDisabled={isUploading}
                >
                  Cancel
                </Button>
                <Button
                  colorScheme="green"
                  rounded="full"
                  fontSize="13px"
                  onClick={() => {
                    handleImportClick();
                  }}
                  isDisabled={!isFormValid}
                  isLoading={isUploading}
                  loadingText="Creating..."
                >
                  Upload
                </Button>
              </HStack>
            </HStack>
          </ModalHeader>

          <Divider borderBottom="1px solid rgba(0,0,0,0.20)" mb={4} />

          <VStack w="full" spacing={4} my={6} align="flex-start">
            <VStack align="flex-start" w="full" spacing={4}>
              <VStack w="full" align="flex-start">
                <Heading size="sm">Audience Name</Heading>
                <Input
                  placeholder="Name of the audience"
                  {...register("name")}
                />
              </VStack>
              <VStack w="full" align="flex-start">
                <Heading size="sm">Description (Optional)</Heading>
                <Input
                  placeholder="Description of the audience"
                  {...register("description")}
                />
              </VStack>
            </VStack>
          </VStack>

          <Badge
            bg="#718096"
            color="white"
            rounded="full"
            mb={2}
            py={1}
            px={3}
            fontSize="12px"
            fontWeight="500"
          >
            Large CSV files may take up to 3 hours to process
          </Badge>
          <VStack align="flex-start" spacing={2} mb={4}>
            <Text fontSize="sm">
              The CSV must include at least first name and email for all
              contacts.
            </Text>
            <Text fontSize="sm">
              Please assign the column headers to the audience fields.
              Unassigned columns will be ignored.
            </Text>
            <Text fontSize="sm" color="#CC2929" fontWeight="600">
              First name and email are required*
            </Text>
          </VStack>

          <ModalBody p={0} overflow="visible">
            <ReactS3Uploader
              style={{ display: "none" }}
              //@ts-ignore
              inputRef={inputRef}
              getSignedUrl={(f, cb) => actions.ADMIN.createUploadUrl(f, cb)}
              onError={(msg) => onUploadError(new Error(msg))}
              onFinish={onUploadFinish}
              accept="text/csv"
              uploadRequestHeaders={(f) => ({
                "Content-Type": f.type,
                "x-amz-acl": "private",
              })}
            />

            {!parsedHeaders.length ? (
              <Box
                {...getRootProps()}
                bg="gray.100"
                borderColor={isDragActive ? "green.500" : "gray.300"}
                borderRadius="md"
                py={12}
                textAlign="center"
                cursor="pointer"
              >
                <input {...getInputProps()} />
                <Icon as={FaFileCsv} boxSize={8} color="gray.400" />
                <Text fontWeight="600" mt={6}>
                  UPLOAD CSV FILE
                </Text>
                <Text>Drop CSV file here or click to select</Text>
              </Box>
            ) : (
              <VStack align="stretch" spacing={2} mt={4}>
                <Box
                  overflowX="auto"
                  overflowY="auto"
                  maxH="500px"
                  border="1px solid #E2E8F0"
                  borderRadius="md"
                >
                  <Table
                    variant="simple"
                    size="md"
                    whiteSpace="nowrap"
                    minW="max-content"
                  >
                    <Thead>
                      <Tr>
                        {parsedHeaders.map((_, idx) => {
                          const others = mapping.filter((_, i) => i !== idx);
                          const available = AUDIENCE_SCHEMA_FIELDS.filter(
                            (f) =>
                              f.value === mapping[idx] ||
                              !others.includes(f.value)
                          );

                          return (
                            <Th key={idx} px={2} py={1}>
                              <FormControl>
                                <ChakraSelect
                                  size="xs"
                                  value={mapping[idx]}
                                  onChange={(e) => {
                                    const m = [...mapping];
                                    m[idx] = e.target.value;
                                    setMapping(m);
                                  }}
                                >
                                  <option value="">None</option>
                                  {available.map((f) => (
                                    <option key={f.value} value={f.value}>
                                      {f.label}
                                    </option>
                                  ))}
                                </ChakraSelect>
                              </FormControl>
                            </Th>
                          );
                        })}
                      </Tr>
                      <Tr>
                        {parsedHeaders.map((col, idx) => (
                          <Th key={idx} px={2} py={2} fontSize="sm">
                            {col}
                          </Th>
                        ))}
                      </Tr>
                    </Thead>
                    <Tbody>
                      {parsedRows.map((row, ri) => (
                        <Tr key={ri}>
                          {row.map((cell, ci) => (
                            <Td key={ci} px={2} py={3} fontSize="14px">
                              {cell}
                            </Td>
                          ))}
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              </VStack>
            )}
          </ModalBody>
        </Box>
        <ModalFooter />
      </ModalContent>
    </Modal>
  );
};

const CreateAudienceFromCsvDialogWrapper = ({
  onSubmit,
}: {
  onSubmit: (audienceData: {
    id: string;
    name: string;
    description: string;
    file_key: string;
    mapping: string[];
  }) => void;
}) => {
  const { isOpen, close } = useContext(CreateAudienceFromCsvDialogContext);

  return (
    <CreateAudienceFromCsvDialog
      onSubmit={onSubmit}
      isOpen={isOpen}
      close={close}
    />
  );
};

export const withCreateAudienceFromCsvDialog =
  <P extends object>(Component: React.ComponentType<P>) =>
  (
    props: P & {
      onChange?: (audienceData: {
        id: string;
        name: string;
        description: string;
        file_key: string;
        mapping: string[];
      }) => void;
    }
  ) => {
    return (
      <DialogProvider context={CreateAudienceFromCsvDialogContext}>
        <Component {...props} />
        <CreateAudienceFromCsvDialogWrapper
          onSubmit={props.onChange || (() => {})}
        />
      </DialogProvider>
    );
  };

export default CreateAudienceFromCsvDialog;
